
import gql from 'graphql-tag';
import CancelDisputeDialog from '@/components/_dialogs/CancelDisputeDialog.vue';
import DisputeDialog from '@/components/_dialogs/DisputeDialog.vue';
import AttachmentsDialog from '@/components/_dialogs/AttachmentsDialog.vue';
import { useComponentsInvoiceDisputes_InvoiceDisputesQuery, useCancelInvoiceDisputeMutation, useAssignInvoiceDisputeMutation, Risolto_Document_Attachment_Type_Enum, ComponentsInvoiceDisputes_UserInterfaceDetailsDocument, ComponentsInvoiceDisputes_UserInterfaceDetailsQueryVariables } from '@/generated/graphql';
import { defineComponent, toRefs, ref, reactive, getCurrentInstance } from 'vue';
import InvoiceDisputeHelper from '@/helpers/invoice-dispute';
import { S3_Attachment } from '@/schema/attachment';
import { Notification } from 'element-ui';
import i18n from '@/i18n';
import { invoiceUploadAttachment, invoiceDeleteAttachment } from '@/api/invoice';
import CustomFieldLabel from '@/components/_CustomFields/CustomFieldLabel.vue';
import CustomFieldValue from '@/components/_CustomFields/CustomFieldValue.vue';
import { useResult } from '@vue/apollo-composable';

gql`
query ComponentsInvoiceDisputes_invoiceDisputes ($participant: uuid!, $legalEntity: uuid!, $recipient: uuid!, $invoices: [uuid!]) {
	invoice_disputes: risolto_invoice_dispute(
		where: {
			participant: { _eq: $participant }
			legal_entity: { _eq: $legalEntity }
			recipient: { _eq: $recipient }
			invoice: { _in: $invoices }
			resolved_at: { _is_null: true }
		},
		order_by: { until: asc }
	) {
		participant
		legal_entity
		reference
		until
		dispute_code
		dispute_code_by_reference {
			reference
			code
			default_assignee
			default_days_to_suspend
		}
		tasks(
			where: { type: { _eq: "DISPUTE" }, status: { _eq: "OPEN" } }
			limit: 1
		) {
			participant
			legal_entity
			reference
			due_date
			assignee
			assignee_by_reference {
				reference
				username
			}
			notes(limit: 1) {
				note
			}
		}
		invoice: invoice_by_reference {
			reference
			invoice_number
			open_amount
			total_amount
			amount_currency
			attachments
			custom_fields
			payment_reference
		}
	}
}
`;

gql`
mutation cancelInvoiceDispute (
  $participant: uuid!
  $legal_entity: uuid
  $references: [uuid!]!
  $resolved_at: timestamptz!
  $canceled_by: uuid!
) {
  update_risolto_invoice_dispute(
    where: {
      participant: { _eq: $participant }
      legal_entity: { _eq: $legal_entity }
      reference: { _in: $references }
    },
    _set: {
      resolved_at: $resolved_at
    }
  ) {
    affected_rows
    returning {
      __typename
      reference
      resolved_at
    }
  },
  update_risolto_task(
      where: {
        participant: { _eq: $participant }
        legal_entity: { _eq: $legal_entity }
        invoice_dispute: { _in: $references }
      },
      _set: {
        status: "DONE",
        canceled_at: $resolved_at,
        canceled_by: $canceled_by
      }
    ) {
      affected_rows
      returning {
        __typename
        reference
        status
      }
    }
}
`;

gql`
mutation assignInvoiceDispute (
  $participant: uuid!
  $legal_entity: uuid
  $references: [uuid!]!
  $assignee: uuid # allow 'null' values
) {
  update_risolto_task(
      where: {
        participant: { _eq: $participant }
        legal_entity: { _eq: $legal_entity }
        invoice_dispute: { _in: $references }
      },
      _set: {
        assignee: $assignee
      }
    ) {
      affected_rows
      returning {
        __typename
        reference
        status
      }
    }
}
`;

gql`
query ComponentsInvoiceDisputes_userInterfaceDetails($participant: uuid!) {
	userInterfaceDetails: risolto_participant(
		where: {
			reference: { _eq: $participant }
		}
	) {
		custom_field_definitions: user_interface_details(path: "invoice.custom_field_definitions")
	}
}`;

export default defineComponent({
	apollo: {
		userInterfaceDetails: {
			query: ComponentsInvoiceDisputes_UserInterfaceDetailsDocument,
			variables(): ComponentsInvoiceDisputes_UserInterfaceDetailsQueryVariables {
				return {
					'participant': this.$store.state.risolto.selectedParticipantStore
				};
			},
			update(data) {
				return data.userInterfaceDetails[0];
			}
		}
	},
	components: {
		AttachmentsDialog,
		CancelDisputeDialog,
		DisputeDialog,
		CustomFieldLabel,
		CustomFieldValue
	},
	props: {
		recipient: { type: String, required: true },
		legalEntity: { type: String, required: true },
		invoices: { type: Array, required: true }
	},
	state: {
		selected: 'test'
	},
	setup(props, context) {
		const { legalEntity, recipient, invoices } = toRefs(props);
		const root = getCurrentInstance()?.proxy;

		const variables = reactive({
			participant: root?.$store.state.risolto.selectedParticipantStore,
			legalEntity: legalEntity,
			recipient: recipient,
			invoices: invoices
		});
		const { result, loading, error, refetch } = useComponentsInvoiceDisputes_InvoiceDisputesQuery(variables);
		const invoiceDisputes = useResult(result, [], (data) => data.invoice_disputes);

		// Actions (Extend + Cancel)
		let cancelDisputeDialogVisible = ref(false);
		let disputeDialogVisible = ref(false);
		let selectedInvoices = ref([] as string[]);
		let selectedDisputes = ref([] as string[]);
		let selectedInvoicesDispute = ref({} as any);
		const handleExtend = async (index, row) => {
			selectedDisputes.value = [row.reference];
			selectedInvoices.value = [row.invoice.reference];
			disputeDialogVisible.value = true;
		};
		const handleExtendTheLot = async () => {
			disputeDialogVisible.value = true;
		};
		const handleSelectionChange = async (event) => {
			selectedDisputes.value = event.map(row => row.reference);
			selectedInvoices.value = event.map(row => row.invoice.reference);

			// take first invoice_dispute
			selectedInvoicesDispute.value = event[0];
		};
		const handleCancel = async (index, row) => {
			selectedDisputes.value = [row.reference];
			selectedInvoices.value = [row.invoice.reference];
			cancelDisputeDialogVisible.value = true;
		};
		const handleCancelTheLot = async () => {
			cancelDisputeDialogVisible.value = true;
		};

		const delay = async (time) => {
			return new Promise(resolve => setTimeout(resolve, time));
		};

		const handleCancelConfirm = async () => {
			const { mutate } = useCancelInvoiceDisputeMutation({
				variables: {
					participant: root?.$store.state.risolto.selectedParticipantStore,
					legal_entity: legalEntity.value,
					references: selectedDisputes.value,
					resolved_at: new Date(),
					canceled_by: root?.$store.state.risolto.loggedInUser.reference
				}
			});
			mutate();
			await delay(500);
			refetch();
			context.emit('change');
		};

		const handleExtendConfirm = async () => {
			refetch();
			context.emit('change');
		};

		const handleDialogClose = async () => {
			disputeDialogVisible.value = false;
			refetch();
			context.emit('change');
		};

		const handleCancelDialogClose = async () => {
			cancelDisputeDialogVisible.value = false;
			refetch();
			context.emit('change');
		};

		const isDue = (row) => {
			return InvoiceDisputeHelper.isDue(row.tasks[0].due_date);
		};

		const isOverdue = (row) => {
			return InvoiceDisputeHelper.isOverDue(row.until);
		};

		// Assignee
		const handleAssignInvoiceDispute = async (invoiceDispute, value) => {
			let assignee = value;
			if (value === 'unassigned') {
				assignee = null;
			}
			const { mutate } = useAssignInvoiceDisputeMutation({
				variables: {
					participant: invoiceDispute.participant,
					legal_entity: invoiceDispute.legal_entity,
					references: invoiceDispute.reference,
					assignee: assignee
				}
			});
			mutate();
			await delay(500);
			refetch();
			context.emit('change');
		};

		const handleAssignTheLot = async (assignee) => {
			const { mutate } = useAssignInvoiceDisputeMutation({
				variables: {
					participant: root?.$store.state.risolto.selectedParticipantStore,
					legal_entity: legalEntity.value,
					references: selectedDisputes.value,
					assignee: assignee
				}
			});
			mutate();
			await delay(500);
			refetch();
			context.emit('change');
		};

		// Attachments
		let attachmentsDialogFileList = reactive({value: [] as S3_Attachment[]});
		let attachmentsDialogInvoice = ref('');
		let attachmentsDialogVisible = ref(false);
		const handleAttachments = async (index, row) => {
			attachmentsDialogInvoice = ref(row.invoice.reference);
			attachmentsDialogFileList.value = row.invoice.attachments;
			attachmentsDialogVisible.value = true;
		};

		const handleAttachmentUpload = async (attachment: S3_Attachment) => {
			try {
				await invoiceUploadAttachment(
					root?.$store.state.risolto.selectedParticipantStore,
					legalEntity.value,
					recipient.value,
					attachmentsDialogInvoice.value,
					Risolto_Document_Attachment_Type_Enum.Pdf,
					attachment.name,
					attachment
				);
				refetch();
				attachmentsDialogFileList.value = [...attachmentsDialogFileList.value, attachment];
			} catch (e) {
				Notification({
					title: i18n.t('invoice-disputes.error') as string,
					message: i18n.t('invoice-disputes.attachment-can-not-be-uploaded') as string,
					type: 'error'
				});
				return;
			}
		};

		const handleAttachmentRemove = async (attachment: S3_Attachment) => {
			try {
				await invoiceDeleteAttachment(attachment.key);
				refetch();
				attachmentsDialogFileList.value = attachmentsDialogFileList.value.filter((item) => item.key !== attachment.key);
			} catch (e) {
				Notification({
					title: i18n.t('invoice-disputes.error') as string,
					message: i18n.t('invoice-disputes.attachment-can-not-be-removed') as string,
					type: 'error'
				});
				return;
			}
		};

		return {
			invoiceDisputes,
			loading,
			error,
			handleExtend,
			handleCancel,
			handleExtendConfirm,
			handleCancelConfirm,
			handleDialogClose,
			handleCancelDialogClose,
			selectedDisputes,
			selectedInvoices,
			selectedInvoicesDispute,
			disputeDialogVisible,
			cancelDisputeDialogVisible,
			handleExtendTheLot,
			handleCancelTheLot,
			handleSelectionChange,
			handleAssignInvoiceDispute,
			handleAssignTheLot,
			isDue,
			isOverdue,
			attachmentsDialogFileList,
			attachmentsDialogVisible,
			handleAttachments,
			handleAttachmentUpload,
			handleAttachmentRemove
		};
	}
});

